// src/pages/BookingTimeline.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Stack,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
} from "@mui/material";
import Timeline, {
  TimelineHeaders,
  SidebarHeader,
  DateHeader,
} from "react-calendar-timeline";
import "react-calendar-timeline/dist/style.css";
import "../css/BookingTimeline.css"; // Import separate CSS
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  getDocs,
  addDoc,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import BookingPopup from "../components/BookingPopup";
import {
  getBookingCache,
  saveBookingCache,
  getVehicleCache,
  saveVehicleCache,
} from "../utils/bookingCache";

// Helper: convert input to Date (if already a Date, return it)
const toDate = (val) => (val instanceof Date ? val : new Date(val));
// Helper: returns the start of the day for a given date
const getStartOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());
// Duration constants
const ONE_DAY = 24 * 3600 * 1000;
const SEVEN_DAYS = 7 * ONE_DAY;

const BookingTimeline = () => {
  const auth = getAuth();
  const [user, loadingAuth] = useAuthState(auth);
  const shopId = user ? user.uid : null;
  const [shopSlug, setShopSlug] = useState(null);
  const navigate = useNavigate();

  // Default timeline start: one day before today.
  const defaultStartTime = getStartOfDay(new Date()).getTime() - ONE_DAY;

  const initialCachedBookings = getBookingCache() || [];
  const [vehicles, setVehicles] = useState(getVehicleCache() || []);
  const [bookings, setBookings] = useState(initialCachedBookings);

  const [visibleTimeStart, setVisibleTimeStart] = useState(defaultStartTime);
  const [visibleTimeEnd, setVisibleTimeEnd] = useState(
    defaultStartTime + SEVEN_DAYS
  );
  const [jumpDate, setJumpDate] = useState(moment().format("YYYY-MM-DD"));
  const [openDialog, setOpenDialog] = useState(false);
  const [openBlockDialog, setOpenBlockDialog] = useState(false);
  const [editBlockedData, setEditBlockedData] = useState(null);
  const [manualBookingData, setManualBookingData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    vehicleId: "",
    startTime: "",
    endTime: "",
    fullPrice: "",
    deposit: "",
  });
  const [blockData, setBlockData] = useState({
    vehicleId: "",
    startTime: "",
    endTime: "",
    reason: "",
  });
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draggedItemId, setDraggedItemId] = useState(null);

  // Smooth scroll animation for timeline
  const smoothScrollTo = (targetStart, targetEnd, duration = 500) => {
    const startTime = performance.now();
    const initialStart = visibleTimeStart;
    const initialEnd = visibleTimeEnd;
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const t = Math.min(1, elapsed / duration);
      const easeT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      setVisibleTimeStart(initialStart + (targetStart - initialStart) * easeT);
      setVisibleTimeEnd(initialEnd + (targetEnd - initialEnd) * easeT);
      if (t < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  };

  const fetchShopSlug = async (userId) => {
    const ownerDocRef = doc(firestore, "users", userId);
    const ownerSnap = await getDoc(ownerDocRef);
    if (ownerSnap.exists()) {
      return ownerSnap.data().shopId; // Slug is stored in shopOwners
    } else {
      console.error("No shop assigned to this user.");
      return null;
    }
  };

  useEffect(() => {
    if (user) {
      fetchShopSlug(user.uid).then((slug) => {
        if (slug) setShopSlug(slug);
      });
    }
  }, [user]);

  const handleToday = () => {
    const today = getStartOfDay(new Date());
    const targetStart = today.getTime();
    const targetEnd = targetStart + SEVEN_DAYS;
    setJumpDate(moment(today).format("YYYY-MM-DD"));
    smoothScrollTo(targetStart, targetEnd);
  };

  const handleJumpDateChange = (e) => {
    const newDate = new Date(e.target.value);
    setJumpDate(e.target.value);
    const targetStart = getStartOfDay(newDate).getTime();
    const targetEnd = targetStart + SEVEN_DAYS;
    smoothScrollTo(targetStart, targetEnd);
  };

  const handleTimeChange = (start, end, updateScrollCanvas) => {
    setVisibleTimeStart(start);
    setVisibleTimeEnd(end);
    updateScrollCanvas(start, end);
  };

  // Fetch vehicles: include the model in each group object.
  useEffect(() => {
    if (!shopSlug) return;

    const fetchVehicles = async () => {
      try {
        const snapshot = await getDocs(
          collection(firestore, "rentalShops", shopSlug, "vehicles")
        );
        const vehicleList = snapshot.docs.map((doc) => {
          const data = doc.data();
          const vehicleData = {
            id: doc.id,
            title: `${data.model} - ${data.plateNr}`,
            model: data.model,
            brand: data.brand,
            plateNr: data.plateNr,
          };
          return vehicleData;
        });
        setVehicles(vehicleList);
        saveVehicleCache(vehicleList);
      } catch (err) {
        console.error("Error fetching vehicles:", err);
      }
    };

    fetchVehicles();
  }, [shopSlug]);

  // Fetch bookings
  useEffect(() => {
    if (!shopSlug) return;
    const cached = getBookingCache();
    if (cached?.length) setBookings(cached);

    const fetchBookings = async () => {
      try {
        const snapshot = await getDocs(
          collection(firestore, "rentalShops", shopSlug, "bookings")
        );

        const data = snapshot.docs.map((doc) => {
          const b = doc.data();
          return {
            id: doc.id,
            group: String(b.vehicle?.vehicleId || "unknown"),
            title: b.label || "Booking",
            start_time: toDate(b.startDate).getTime(),
            end_time: toDate(b.endDate).getTime(),
            type: b.type,
            raw: b,
          };
        });

        setBookings(data);
        saveBookingCache(data);
      } catch (error) {
        console.error("Error fetching bookings:", error);
      }
    };

    fetchBookings();
  }, [shopSlug]);

  const handleBlockSubmit = async (e) => {
    e.preventDefault();
    const blockedBooking = {
      type: "blocked",
      status: "blocked",
      shopId: shopSlug,
      statusHistory: [{ status: "blocked", at: new Date() }],
      bookingStage: "upcoming",
      startDate: new Date(blockData.startTime).toISOString(),
      endDate: new Date(blockData.endTime).toISOString(),
      rentalDays: Math.max(
        1,
        Math.ceil(
          moment(blockData.endTime).diff(
            moment(blockData.startTime),
            "days",
            true
          )
        )
      ),
      label: `Blocked | ${moment(blockData.startTime).format("MMM D")}`,
      reason: blockData.reason || "",
      vehicle: {
        vehicleId: blockData.vehicleId,
        vehicleType:
          vehicles.find((v) => v.id === blockData.vehicleId)?.model || "",
      },
      shop: {
        shopId: shopSlug,
        name: "",
      },
      source: {
        bookingMethod: "manual",
        referralCode: null,
        shopAffiliate: null,
        deviceType: null,
      },
      flags: {
        updatedManually: true,
        adminNote: "",
        blocked: true,
        manuallyReviewed: false,
        fraudSuspected: false,
      },
      analytics: {
        rentalHours: moment(blockData.endTime).diff(
          moment(blockData.startTime),
          "hours",
          true
        ),
        leadTimeHours: moment(blockData.startTime).diff(moment(), "hours"),
      },
      createdBy: {
        uid: user?.uid,
        role: "shopOwner",
      },
      createdAt: new Date(),
      timeline: {
        createdAt: new Date(),
        updatedAt: null,
        confirmedAt: null,
        cancelledAt: null,
        completedAt: null,
      },
      timezone: "Asia/Manila",
    };
    try {
      const docRef = await addDoc(
        collection(firestore, "rentalShops", shopSlug, "bookings"),
        blockedBooking
      );

      setBookings((prev) => [
        ...prev,
        {
          id: docRef.id,
          group: String(blockData.vehicleId),
          title: blockedBooking.label,
          start_time: new Date(blockData.startTime).getTime(),
          end_time: new Date(blockData.endTime).getTime(),
          type: "blocked",
          raw: blockedBooking,
        },
      ]);

      setOpenBlockDialog(false);
      setBlockData({ vehicleId: "", startTime: "", endTime: "", reason: "" });
    } catch (error) {
      alert("Failed to block vehicle");
    }
  };

  // Create background items for each vehicle group to highlight today's range.
  const todayStart = getStartOfDay(new Date()).getTime();
  const todayEnd = todayStart + ONE_DAY;
  const backgroundItems = vehicles.map((vehicle) => ({
    id: `bg-${vehicle.id}`,
    group: vehicle.id,
    title: "",
    start_time: todayStart,
    end_time: todayEnd,
    itemProps: {
      style: { backgroundColor: "rgba(0, 0, 255, 0.2)" },
    },
  }));

  const getItemStyle = (item, isSelected = false) => {
    const raw = item.raw || {};
    const type = raw.type || "booking";

    let bgColor = "#F4F4F7";
    if (type === "blocked") {
      bgColor = "#ff4d4d";
    } else if (isSelected) {
      bgColor = "gold"; // yellow highlight
    } else if (raw.status === "active") {
      bgColor = "#CCCCD9";
    } else if (
      raw.status === "booked" &&
      (moment(item.start_time).isSame(moment(), "day") ||
        moment(item.end_time).isSame(moment(), "day"))
    ) {
      bgColor = "#97BBD7";
    }

    return {
      backgroundColor: bgColor,
      color: type === "blocked" ? "white" : "black",
      borderRadius: "4px",
      border: isSelected ? "2px solid black" : "0.4px solid #000",
      zIndex: 100,
    };
  };

  // Map bookings to include custom itemProps for Timeline.
  const coloredItems = bookings
    .filter((item) => item.raw?.status !== "cancelled")
    .map((item) => {
      const isSelected = selectedId === item.id;
      const isBlocked = item.raw?.type === "blocked";

      return {
        ...item,
        selected: isSelected,
        canMove: !isBlocked,
        canChangeGroup: !isBlocked,
        itemProps: {
          style: getItemStyle(item, isSelected),
          className: isBlocked
            ? isSelected
              ? "booking-blocked selected"
              : "booking-blocked"
            : isSelected
            ? "booking-normal selected"
            : "booking-normal",
        },
      };
    });

  const handleItemClick = (itemId, e, time) => {
    if (selectedId === itemId) {
      const booking = bookings.find((b) => b.id === itemId);
      if (booking) setSelectedBooking(booking); // open popup
    }
  };

  // When item is actually moved
  const handleItemMove = async (itemId, dragTime, newGroupId) => {
    console.log("handle item move");
    setDraggedItemId(itemId); // prevent popup

    const booking = bookings.find((b) => b.id === itemId);
    if (!booking) return;
    console.log("Booking found...");

    if (booking.start_time < Date.now()) {
      alert("Only future bookings can be reassigned.");
      return;
    }

    console.log("booking in the future...");

    const newGroupIdStr = String(newGroupId);
    const newGroup = vehicles.find((v) => v.id === newGroupIdStr);
    const oldGroup = vehicles.find((v) => v.id === String(booking.group));

    console.log("newGroupId: ", newGroupId);
    console.log("newGroup: ", newGroup);
    console.log("booking.group: ", booking.group);
    console.log("oldGroup: ", oldGroup);

    if (!oldGroup || !newGroup) return;

    console.log("oldGroup & newGroup exist...");

    if (newGroup.model !== oldGroup.model) {
      alert("Booking can only be reassigned to a vehicle of the same model.");
      return;
    }

    console.log("vehicles are same model...");

    const duration = booking.end_time - booking.start_time;
    const newStartTime = dragTime;
    const newEndTime = dragTime + duration;

    const updatedBooking = {
      ...booking,
      group: newGroup.id,
      start_time: newStartTime,
      end_time: newEndTime,
      model: newGroup.model,
    };

    setBookings((prev) =>
      prev.map((b) => (b.id === itemId ? updatedBooking : b))
    );

    console.log("Bookings set...");

    try {
      const bookingRef = doc(
        firestore,
        "rentalShops",
        shopSlug,
        "bookings",
        itemId
      );
      await updateDoc(bookingRef, {
        "vehicle.vehicleId": newGroup.id,
        "vehicle.vehicleType": newGroup.model,
        startDate: new Date(newStartTime).toISOString(),
        endDate: new Date(newEndTime).toISOString(),
        shopId: shopSlug,
        ownerId: shopId,
      });
      const updated = {
        ...updatedBooking,
        raw: {
          ...booking.raw,
          vehicle: {
            ...booking.raw.vehicle,
            vehicleId: newGroup.id,
            vehicleType: newGroup.model,
          },
          startDate: new Date(newStartTime).toISOString(),
          endDate: new Date(newEndTime).toISOString(),
        },
      };
      console.log("Booking doc updated!");
      setBookings((prev) => prev.map((b) => (b.id === itemId ? updated : b)));
      saveBookingCache(bookings.map((b) => (b.id === itemId ? updated : b)));
      console.log("Booking doc updated and cache refreshed!");
    } catch (error) {
      console.error("Error reassigning booking:", error);
      alert("Failed to reassign booking. Please try again.");
      setBookings((prev) => prev.map((b) => (b.id === itemId ? booking : b)));
    }
  };

  const handleItemSelect = (itemId, e, time) => {
    if (selectedId === itemId) {
      const booking = bookings.find((b) => b.id === itemId);
      if (booking) setSelectedBooking(booking); // second click opens popup
    } else {
      setSelectedId(itemId); // first click selects
    }
  };

  const handleItemDeselect = () => {
    setSelectedId(null);
  };

  const closeBookingPopup = () => {
    setSelectedBooking(null);
    // setSelectedId(null);
  };

  const GroupRenderer = ({ group }) => {
    return (
      <div
        style={{
          cursor: "pointer",
          padding: "5px",
          textAlign: "center",
          fontWeight: "bold",
        }}
        onClick={() => navigate(`/vehicle/${group.id}`)}
      >
        {group.title}
      </div>
    );
  };

  // Manual Booking Submission Logic
  const handleManualBookingSubmit = async (e) => {
    e.preventDefault();

    const {
      firstName,
      lastName,
      email,
      vehicleId,
      startTime,
      endTime,
      fullPrice,
      deposit,
    } = manualBookingData;

    if (!firstName || !vehicleId || !startTime || !endTime || !fullPrice) {
      alert("Please fill in all required fields.");
      return;
    }

    const fullName = `${firstName} ${lastName}`.trim();
    const rentalDays = Math.max(
      1,
      Math.ceil(moment(endTime).diff(moment(startTime), "days", true))
    );

    const dailyRate = Number(fullPrice) / rentalDays;
    const baseCost = Number(fullPrice);

    const vehicle = vehicles.find((v) => v.id === vehicleId);
    const vehicleType = vehicle?.model || "Unknown";
    const vehicleBrand = vehicle?.brand || "Unknown";
    const shopName = "Your Shop Name"; // Replace or fetch dynamically if needed

    const bookingData = {
      type: "booking",
      status: "booked",
      shopId: shopSlug,
      statusHistory: [{ status: "booked", at: new Date() }],
      bookingStage: "upcoming",
      startDate: new Date(startTime).toISOString(),
      endDate: new Date(endTime).toISOString(),
      rentalDays,
      label: `${vehicleBrand} ${vehicleType} | ${fullName} | ${moment(
        startTime
      ).format("MMM D")}`,
      vehicle: {
        vehicleId,
        vehicleType,
      },
      customer: {
        firstName,
        lastName,
        fullName,
        email,
        nationality: null,
      },
      shop: {
        shopId: shopSlug,
        name: shopName,
      },
      source: {
        bookingMethod: "manual",
        referralCode: null,
        shopAffiliate: null,
        deviceType: null,
      },
      bill: {
        dailyRate,
        baseCost,
        extraHoursRate: 0,
        extraHoursCost: 0,
        rentalDays,
        insuranceCost: 0,
        totalCost: baseCost,
        currency: "PHP",
      },
      flags: {
        adminNote: "",
        updatedManually: true,
        blocked: false,
        fraudSuspected: false,
        manuallyReviewed: true,
      },
      analytics: {
        rentalHours: moment(endTime).diff(moment(startTime), "hours", true),
        leadTimeHours: moment(startTime).diff(moment(), "hours"),
      },
      policySnapshot: {
        cancellationInsurance: false,
        insuranceAmount: 0,
        agreedAt: new Date(),
        version: "v1.0",
      },
      marketingConsent: {
        emailPromotions: false,
        termsAccepted: false,
        acceptedAt: null,
      },
      deposit: Number(deposit) || 0,
      paid: false,
      paymentMethod: "",
      createdBy: {
        uid: user?.uid,
        role: "shopOwner",
      },
      createdAt: new Date(),
      timeline: {
        createdAt: new Date(),
        updatedAt: null,
        confirmedAt: null,
        cancelledAt: null,
        completedAt: null,
      },
      timezone: "Asia/Manila",
    };

    try {
      const docRef = await addDoc(
        collection(firestore, "rentalShops", shopSlug, "bookings"),
        bookingData
      );

      setBookings((prev) => [
        ...prev,
        {
          id: docRef.id,
          group: String(vehicleId),
          title: bookingData.label,
          start_time: new Date(startTime).getTime(),
          end_time: new Date(endTime).getTime(),
          type: "booking",
          raw: bookingData,
        },
      ]);

      setOpenDialog(false);
      setManualBookingData({
        firstName: "",
        lastName: "",
        email: "",
        vehicleId: "",
        startTime: "",
        endTime: "",
        fullPrice: "",
        deposit: "",
      });
    } catch (error) {
      console.error("Error adding manual booking:", error);
      alert("Error adding manual booking. Please try again.");
    }
  };

  if (!shopSlug) {
    return (
      <Box>
        <Container sx={{ mt: 6, mb: 8, textAlign: "center" }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading authentication...</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      {/* Date input, Today, and Manual Booking buttons */}
      <Box
        sx={{
          mb: 2,
          textAlign: "center",
          display: "flex",
          gap: 2,
          justifyContent: "left",
        }}
      >
        <Typography
          variant="h4"
          align="center"
          display={"flex"}
          alignItems={"center"}
        >
          Timeline
        </Typography>
        <TextField
          type="date"
          value={jumpDate}
          onChange={handleJumpDateChange}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="outlined" onClick={handleToday}>
          Today
        </Button>
        <Button variant="outlined" onClick={() => setOpenDialog(true)}>
          + Booking
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setOpenBlockDialog(true)}
        >
          + Block
        </Button>
      </Box>

      {/* Block vehicle Dialog */}
      <Dialog
        open={openBlockDialog}
        onClose={() => setOpenBlockDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Block Vehicle</DialogTitle>
        <form onSubmit={handleBlockSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  select
                  fullWidth
                  label="Select Vehicle"
                  value={blockData.vehicleId}
                  onChange={(e) =>
                    setBlockData((prev) => ({
                      ...prev,
                      vehicleId: e.target.value,
                    }))
                  }
                >
                  {vehicles.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.title}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  required
                  fullWidth
                  type="datetime-local"
                  label="Start"
                  InputLabelProps={{ shrink: true }}
                  value={blockData.startTime}
                  onChange={(e) =>
                    setBlockData((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  required
                  fullWidth
                  type="datetime-local"
                  label="End"
                  InputLabelProps={{ shrink: true }}
                  value={blockData.endTime}
                  onChange={(e) =>
                    setBlockData((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason"
                  value={blockData.reason}
                  onChange={(e) =>
                    setBlockData((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenBlockDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              Save Block
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Manual Booking Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Manual Booking</DialogTitle>
        <form onSubmit={handleManualBookingSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  value={manualBookingData.firstName}
                  onChange={(e) =>
                    setManualBookingData((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={manualBookingData.lastName}
                  onChange={(e) =>
                    setManualBookingData((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={manualBookingData.email}
                  onChange={(e) =>
                    setManualBookingData((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  select
                  fullWidth
                  label="Select Vehicle"
                  value={manualBookingData.vehicleId}
                  onChange={(e) =>
                    setManualBookingData((prev) => ({
                      ...prev,
                      vehicleId: e.target.value,
                    }))
                  }
                >
                  {vehicles.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.title}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  required
                  fullWidth
                  type="datetime-local"
                  label="Start Time"
                  InputLabelProps={{ shrink: true }}
                  value={manualBookingData.startTime}
                  onChange={(e) =>
                    setManualBookingData((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  required
                  fullWidth
                  type="datetime-local"
                  label="End Time"
                  InputLabelProps={{ shrink: true }}
                  value={manualBookingData.endTime}
                  onChange={(e) =>
                    setManualBookingData((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  required
                  fullWidth
                  type="number"
                  label="Full Price"
                  value={manualBookingData.fullPrice}
                  onChange={(e) =>
                    setManualBookingData((prev) => ({
                      ...prev,
                      fullPrice: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Deposit (optional)"
                  value={manualBookingData.deposit}
                  onChange={(e) =>
                    setManualBookingData((prev) => ({
                      ...prev,
                      deposit: e.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="secondary">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Add Booking
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Timeline */}
      <Box sx={{ height: "80vh" }}>
        <Timeline
          groups={vehicles.map((v) => ({ ...v, id: String(v.id) }))}
          items={coloredItems}
          backgroundItems={vehicles.map((vehicle) => ({
            id: `bg-${vehicle.id}`,
            group: vehicle.id,
            title: vehicle.title,
            start_time: todayStart,
            end_time: todayEnd,
            itemProps: {
              style: { backgroundColor: "rgba(0, 0, 255, 0.2)" },
            },
          }))}
          visibleTimeStart={visibleTimeStart}
          visibleTimeEnd={visibleTimeEnd}
          onTimeChange={handleTimeChange}
          defaultTimeStart={visibleTimeStart}
          defaultTimeEnd={visibleTimeEnd}
          timeSteps={{ second: 0, minute: 0, hour: 0, day: 1 }}
          sidebarWidth={150}
          groupRenderer={GroupRenderer}
          onItemMove={handleItemMove}
          onItemSelect={handleItemSelect}
          onItemDeselect={handleItemDeselect}
          onItemClick={handleItemClick}
          selected={selectedId ? [selectedId] : []}
          canMove={true}
          canChangeGroup={true}
        >
          <TimelineHeaders>
            <SidebarHeader>
              {({ getRootProps }) => (
                <div
                  {...getRootProps()}
                  className="timeline-sidebar-header"
                  style={{
                    backgroundColor: "black",
                    color: "white",
                    width: "150px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {moment(visibleTimeStart).format("MMMM YYYY")}
                </div>
              )}
            </SidebarHeader>
            <DateHeader
              unit="day"
              labelFormat="ddd DD"
              intervalRenderer={({ getIntervalProps, intervalContext }) => (
                <div
                  {...getIntervalProps({
                    style: {
                      width: 60,
                      height: 28,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "black",
                      color: "white",
                    },
                  })}
                >
                  {intervalContext.intervalText}
                </div>
              )}
            />
          </TimelineHeaders>
        </Timeline>
      </Box>

      {selectedBooking && selectedBooking.type === "blocked" && (
        <Dialog
          open={true}
          onClose={() => {
            setSelectedBooking(null);
            setEditBlockedData(null);
            setSelectedId(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Blocked Period</DialogTitle>
          <DialogContent dividers>
            {editBlockedData ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Reason"
                    value={editBlockedData.reason}
                    onChange={(e) =>
                      setEditBlockedData((prev) => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Start"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={moment(editBlockedData.startTime).format(
                      "YYYY-MM-DDTHH:mm"
                    )}
                    onChange={(e) =>
                      setEditBlockedData((prev) => ({
                        ...prev,
                        startTime: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="End"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={moment(editBlockedData.endTime).format(
                      "YYYY-MM-DDTHH:mm"
                    )}
                    onChange={(e) =>
                      setEditBlockedData((prev) => ({
                        ...prev,
                        endTime: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Grid>
              </Grid>
            ) : (
              <>
                <Typography gutterBottom>
                  Reason: {selectedBooking.raw.reason || "No reason provided"}
                </Typography>
                <Typography gutterBottom>
                  From: {moment(selectedBooking.start_time).format("LLL")}
                </Typography>
                <Typography gutterBottom>
                  To: {moment(selectedBooking.end_time).format("LLL")}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {editBlockedData ? (
              <>
                <Button
                  onClick={async () => {
                    try {
                      const bookingRef = doc(
                        firestore,
                        "rentalShops",
                        shopSlug,
                        "bookings",
                        selectedBooking.id
                      );
                      await updateDoc(bookingRef, {
                        reason: editBlockedData.reason,
                        startDate: new Date(
                          editBlockedData.startTime
                        ).toISOString(),
                        endDate: new Date(
                          editBlockedData.endTime
                        ).toISOString(),
                      });
                      // Update in local state
                      setBookings((prev) =>
                        prev.map((b) =>
                          b.id === selectedBooking.id
                            ? {
                                ...b,
                                start_time: new Date(
                                  editBlockedData.startTime
                                ).getTime(),
                                end_time: new Date(
                                  editBlockedData.endTime
                                ).getTime(),
                                raw: {
                                  ...b.raw,
                                  reason: editBlockedData.reason,
                                  startDate: new Date(
                                    editBlockedData.startTime
                                  ).toISOString(),
                                  endDate: new Date(
                                    editBlockedData.endTime
                                  ).toISOString(),
                                },
                              }
                            : b
                        )
                      );
                      setSelectedBooking(null);
                      setEditBlockedData(null);
                    } catch (err) {
                      alert("Failed to update blocked entry.");
                      console.error(err);
                    }
                  }}
                  color="primary"
                  variant="contained"
                >
                  Save
                </Button>
                <Button onClick={() => setEditBlockedData(null)}>Cancel</Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() =>
                    setEditBlockedData({
                      reason: selectedBooking.raw.reason || "",
                      startTime: selectedBooking.raw.startDate,
                      endTime: selectedBooking.raw.endDate,
                    })
                  }
                >
                  Edit
                </Button>
                <Button
                  color="error"
                  onClick={async () => {
                    await deleteDoc(
                      doc(
                        firestore,
                        "rentalShops",
                        shopSlug,
                        "bookings",
                        selectedBooking.id
                      )
                    );
                    setBookings((prev) =>
                      prev.filter((b) => b.id !== selectedBooking.id)
                    );
                    setSelectedBooking(null);
                    setEditBlockedData(null);
                  }}
                >
                  Delete
                </Button>
                <Button
                  onClick={() => {
                    setSelectedBooking(null);
                    setEditBlockedData(null);
                    setSelectedId(null);
                  }}
                >
                  Close
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}

      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-start" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/inventory?new=true")}
        >
          ADD Motorbike
        </Button>
      </Box>

      {selectedBooking && selectedBooking.type !== "blocked" && (
        <BookingPopup booking={selectedBooking} onClose={closeBookingPopup} />
      )}
    </Container>
  );
};

export default BookingTimeline;
